import React, { useState, useEffect, useMemo } from "react";
import { Table, Tag, Tooltip, Spin } from "antd";
import ReactECharts from "echarts-for-react";
import { useLazyQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import { GET_OPERATIONS_DASHBOARD } from "../../../Queries/OperationsDashboard";
import {
  T, fmtHrs, fmtSec, statusColor, gradients, getGradient,
  KpiCard, SectionHeader, RingProgress, RefreshButton, SelfDashboard, DatePickerBar,
} from "./OpsDashboardShared";

// ─── Admin Dashboard (Team View) ────────────────────────────────────────────

export const AdminDashboard = ({ employees, teamSummary, teamLabel }) => {
  const [expandedRows, setExpandedRows] = useState([]);

  const columns = [
    {
      title: "Employee",
      dataIndex: "employeeName",
      key: "employeeName",
      width: 200,
      fixed: "left",
      render: (val, rec) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
          <div className="ops-avatar" style={{ background: getGradient(employees.indexOf(rec)) }}>{(val || "?")[0]?.toUpperCase()}</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>{val}</div>
            <div style={{ fontSize: 11, color: T.textMuted }}>{rec.designation || rec.department || "—"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "currentStatus",
      key: "currentStatus",
      width: 110,
      render: (val) => (
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          <span className={`ops-status-dot ${statusColor(val)}`} />
          <span style={{ fontSize: 12, fontWeight: 500, color: T.textSecondary }}>{val || "Offline"}</span>
        </span>
      ),
      filters: [
        { text: "Online", value: "online" },
        { text: "Away", value: "away" },
        { text: "Break", value: "break" },
        { text: "Offline", value: "offline" },
      ],
      onFilter: (v, r) => statusColor(r.currentStatus) === v,
    },
    {
      title: "Login",
      dataIndex: "loginTime",
      key: "loginTime",
      width: 90,
      render: (val) => (
        <span style={{ fontSize: 12, fontWeight: 600, color: val ? T.text : T.textMuted }}>
          {val ? new Date(val).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
        </span>
      ),
    },
    {
      title: "Today",
      dataIndex: "hoursToday",
      key: "hoursToday",
      width: 95,
      sorter: (a, b) => a.hoursToday - b.hoursToday,
      defaultSortOrder: "descend",
      render: (val) => <span style={{ fontSize: 13, fontWeight: 700, color: T.indigo }}>{fmtHrs(val)}</span>,
    },
    {
      title: "This Week",
      dataIndex: "hoursThisWeek",
      key: "hoursThisWeek",
      width: 100,
      sorter: (a, b) => a.hoursThisWeek - b.hoursThisWeek,
      render: (val) => <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{fmtHrs(val)}</span>,
    },
    {
      title: "This Month",
      dataIndex: "hoursThisMonth",
      key: "hoursThisMonth",
      width: 100,
      sorter: (a, b) => a.hoursThisMonth - b.hoursThisMonth,
      render: (val) => <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{fmtHrs(val)}</span>,
    },
    {
      title: "This Year",
      dataIndex: "hoursThisYear",
      key: "hoursThisYear",
      width: 100,
      sorter: (a, b) => a.hoursThisYear - b.hoursThisYear,
      render: (val) => <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{fmtHrs(val)}</span>,
    },
    {
      title: "Productive",
      dataIndex: "productiveToday",
      key: "productiveToday",
      width: 100,
      render: (val, rec) => {
        const total = val + (rec.nonProductiveToday || 0);
        const pct = total ? Math.round((val / total) * 100) : 0;
        return (
          <Tooltip title={`${fmtHrs(val)} productive / ${fmtHrs(rec.nonProductiveToday)} non-productive`}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ flex: 1, height: 6, borderRadius: 3, background: T.borderLight, overflow: "hidden" }}>
                <div style={{ height: "100%", borderRadius: 3, background: pct >= 70 ? T.emerald : pct >= 40 ? T.amber : T.rose, width: `${pct}%`, transition: "width 0.5s" }} />
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: pct >= 70 ? T.emerald : pct >= 40 ? T.amber : T.rose, minWidth: 32, textAlign: "right" }}>{pct}%</span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Requests",
      key: "requests",
      width: 130,
      render: (_, rec) => (
        <div style={{ display: "flex", gap: 4 }}>
          <Tooltip title="Completed"><Tag color="success" style={{ borderRadius: 20, fontSize: 11, fontWeight: 700, border: "none" }}>{rec.completedRequests}</Tag></Tooltip>
          <Tooltip title="Pending"><Tag color="warning" style={{ borderRadius: 20, fontSize: 11, fontWeight: 700, border: "none" }}>{rec.pendingRequests}</Tag></Tooltip>
          {rec.onHoldRequests > 0 && <Tooltip title="On Hold"><Tag color="error" style={{ borderRadius: 20, fontSize: 11, fontWeight: 700, border: "none" }}>{rec.onHoldRequests}</Tag></Tooltip>}
          {/* Cancelled is shown explicitly rather than silently dropped —
              it is excluded from Pending, so without this tag the row's
              tags would not add up to Total. */}
          {rec.cancelledRequests > 0 && <Tooltip title="Cancelled"><Tag color="default" style={{ borderRadius: 20, fontSize: 11, fontWeight: 700, border: "none" }}>{rec.cancelledRequests}</Tag></Tooltip>}
        </div>
      ),
    },
    {
      title: "Auto-Detect",
      dataIndex: "autoDetectionRate",
      key: "autoDetectionRate",
      width: 100,
      sorter: (a, b) => (a.autoDetectionRate || 0) - (b.autoDetectionRate || 0),
      render: (val) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <RingProgress percent={val || 0} color={val >= 70 ? T.emerald : val >= 40 ? T.amber : T.rose} size={28} strokeWidth={3} />
          <span style={{ fontSize: 12, fontWeight: 700, color: val >= 70 ? T.emerald : val >= 40 ? T.amber : T.rose }}>{val || 0}%</span>
        </div>
      ),
    },
  ];

  // Team productivity chart
  const topEmpChart = {
    tooltip: { trigger: "axis", backgroundColor: "#fff", borderColor: T.border, textStyle: { color: T.text, fontSize: 12 } },
    legend: { bottom: 0, textStyle: { fontSize: 11, color: T.textSecondary } },
    grid: { top: 20, right: 20, bottom: 40, left: 50 },
    xAxis: {
      type: "category",
      data: employees.slice(0, 12).map((e) => e.employeeName?.split(" ")[0] || ""),
      axisLabel: { fontSize: 10, color: T.textSecondary, rotate: 30 },
      axisLine: { lineStyle: { color: T.border } },
    },
    yAxis: {
      type: "value", name: "Hours",
      axisLine: { show: false }, splitLine: { lineStyle: { color: T.borderLight } },
      axisLabel: { fontSize: 10, color: T.textSecondary },
    },
    series: [
      { name: "Productive", type: "bar", stack: "total", barWidth: 24, itemStyle: { color: T.emerald }, data: employees.slice(0, 12).map((e) => e.productiveToday) },
      { name: "Non-Productive", type: "bar", stack: "total", barWidth: 24, itemStyle: { color: T.rose, borderRadius: [4, 4, 0, 0] }, data: employees.slice(0, 12).map((e) => e.nonProductiveToday) },
    ],
  };

  return (
    <div>
      {/* Team Summary KPIs */}
      {teamSummary && (
        <div className="ops-grid ops-grid-6" style={{ marginBottom: 24 }}>
          <KpiCard label="Team Members" value={teamSummary.totalMembers} color={T.indigo} gradient={gradients[0]} delay={0} />
          <KpiCard label="Present Today" value={teamSummary.presentToday} color={T.emerald} gradient="linear-gradient(135deg, #10b981, #14b8a6)" delay={60} />
          <KpiCard label="On Leave" value={teamSummary.onLeaveToday} color={T.amber} gradient="linear-gradient(135deg, #f59e0b, #f97316)" delay={120} />
          <KpiCard label="Absent" value={teamSummary.absentToday} color={T.rose} gradient="linear-gradient(135deg, #f43f5e, #e11d48)" delay={180} />
          <KpiCard label="Avg Hours Today" value={fmtHrs(teamSummary.avgHoursToday)} color={T.sky} gradient="linear-gradient(135deg, #0ea5e9, #06b6d4)" delay={240} />
          <KpiCard label="Requests Done" value={teamSummary.totalRequestsCompleted} sub={`${teamSummary.totalRequestsPending} pending${teamSummary.totalRequestsCancelled ? ` \u00b7 ${teamSummary.totalRequestsCancelled} cancelled` : ""}`} color={T.violet} gradient={gradients[5]} delay={300} />
        </div>
      )}

      {/* Team Productivity Chart */}
      {employees.length > 0 && (
        <>
          <SectionHeader title="Team Productivity — Today" subtitle="Productive vs non-productive hours per employee" />
          <div className="ops-card" style={{ padding: 20, marginBottom: 20 }}>
            <ReactECharts option={topEmpChart} style={{ height: 280 }} />
          </div>
        </>
      )}

      {/* Employee Table */}
      <SectionHeader title={teamLabel || "Operations Team"} subtitle={`${employees.length} employees`} />
      <div className="ops-card ops-table" style={{ overflow: "hidden" }}>
        <Table
          columns={columns}
          dataSource={employees}
          rowKey="userId"
          size="small"
          scroll={{ x: 1300 }}
          pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ["10", "25", "50", "100"], style: { padding: "12px 16px" } }}
          expandable={{
            expandedRowKeys: expandedRows,
            onExpandedRowsChange: setExpandedRows,
            expandedRowRender: (record) => (
              <div style={{ padding: 20 }}>
                <SelfDashboard emp={record} />
              </div>
            ),
          }}
        />
      </div>
    </div>
  );
};

// ─── Main Component (Admin Only — Operations Department) ────────────────────

const OperationsDashboard = () => {
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const userDepartment = loginUser?.department || "Operations";

  const [department, setDepartment] = useState(userDepartment);
  const [empStatus, setEmpStatus] = useState("Active");
  const [selectedDate, setSelectedDate] = useState(null); // null = today

  const [fetchDashboard, { data, loading, error }] = useLazyQuery(GET_OPERATIONS_DASHBOARD, {
    fetchPolicy: "no-cache",
  });

  const loadData = (dept, status, date) => {
    const variables = {
      view: "admin",
      department: dept ?? department,
      employeestatus: status ?? empStatus,
      loginUserId: loginUser?.id,
    };
    const d = date !== undefined ? date : selectedDate;
    if (d) variables.date = d;
    fetchDashboard({ variables });
  };

  // Set department to user's own department when user data loads
  const initializedRef = React.useRef(false);
  useEffect(() => {
    if (!initializedRef.current && loginUser?.department) {
      initializedRef.current = true;
      setDepartment(loginUser.department);
      loadData(loginUser.department, empStatus, selectedDate);
      return;
    }
  }, [loginUser?.department]);

  useEffect(() => {
    if (!initializedRef.current) loadData();
  }, []);

  const dashData = data?.getOperationsDashboard;
  const employees = dashData?.employees || [];
  const teamSummary = dashData?.teamSummary;

  // Extract unique departments from results for filter dropdown
  const departments = useMemo(() => {
    return ["Operations", "Sales", "IT", "HR", "Finance", "Marketing", "Admin", "Management"];
  }, []);

  const handleDeptChange = (e) => {
    const val = e.target.value;
    setDepartment(val);
    loadData(val, empStatus, selectedDate);
  };

  const handleStatusChange = (e) => {
    const val = e.target.value;
    setEmpStatus(val);
    loadData(department, val, selectedDate);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    loadData(department, empStatus, date);
  };

  const selectStyle = {
    padding: "8px 14px",
    borderRadius: 10,
    border: `1px solid ${T.border}`,
    fontSize: 13,
    color: T.text,
    background: T.card,
    fontWeight: 500,
    outline: "none",
    cursor: "pointer",
    minWidth: 150,
  };

  return (
    <div style={{ padding: "24px 28px", background: T.bg, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: T.text, margin: 0, letterSpacing: "-0.5px" }}>
            Operations Dashboard
          </h1>
          <p style={{ fontSize: 13, color: T.textSecondary, margin: "4px 0 0" }}>
            {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <select value={department} onChange={handleDeptChange} style={selectStyle}>
            <option value="">All Departments</option>
            {departments.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <select value={empStatus} onChange={handleStatusChange} style={selectStyle}>
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Terminated">Terminated</option>
            <option value="Resigned">Resigned</option>
          </select>
          <DatePickerBar value={selectedDate} onChange={handleDateChange} />
          <RefreshButton loading={loading} onClick={() => loadData()} />
        </div>
      </div>

      {/* Loading */}
      {loading && !dashData && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
          <Spin size="large" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="ops-card" style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: T.rose, marginBottom: 8 }}>Failed to load dashboard</div>
          <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 20 }}>{error.message}</div>
          <button className="ops-refresh-btn" onClick={() => loadData()}>Try Again</button>
        </div>
      )}

      {/* Content */}
      {dashData && !loading && (
        <AdminDashboard employees={employees} teamSummary={teamSummary} teamLabel={department} />
      )}
    </div>
  );
};

export default OperationsDashboard;
